import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Icon,
  Segment,
  Menu,
  Dropdown,
  Checkbox,
  Radio,
  Input,
} from "semantic-ui-react";
import request from "../../api";
import { useTranslation } from "react-i18next";
import {
  escapeScriptTag,
  functionHTMLGenerate,
} from "@fortune-sheet/core";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { Modal, Button } from "semantic-ui-react";
import { normalizeFormulaDecimalLiterals } from "./spreadsheetFormulaUtils";
import {
  applyThousandsFormat,
  getNumberFormat,
} from "./spreadsheetNumberUtils";

// Returns true when a cell's computed value is a #VALUE! error string
const isValueError = (v) => {
  if (v === null || v === undefined) return false;
  return String(v).trim() === "#VALUE!";
};

const getCellReference = (row, column) => {
  let columnName = "";
  let currentColumn = column + 1;

  while (currentColumn > 0) {
    const remainder = (currentColumn - 1) % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    currentColumn = Math.floor((currentColumn - 1) / 26);
  }

  return `${columnName}${row + 1}`;
};

const getCellRangeReference = (
  anchorRow,
  anchorColumn,
  focusRow,
  focusColumn
) => {
  const startRow = Math.min(anchorRow, focusRow);
  const endRow = Math.max(anchorRow, focusRow);
  const startColumn = Math.min(anchorColumn, focusColumn);
  const endColumn = Math.max(anchorColumn, focusColumn);
  const startReference = getCellReference(startRow, startColumn);
  const endReference = getCellReference(endRow, endColumn);

  return startReference === endReference
    ? startReference
    : `${startReference}:${endReference}`;
};

const getEditorValue = (editor) =>
  editor?.value ?? editor?.textContent ?? editor?.innerText ?? "";

const isActivelyEditingSpreadsheet = (element) => {
  if (!element) return false;

  const isEditable =
    element.tagName === "INPUT" ||
    element.tagName === "TEXTAREA" ||
    element.contentEditable === "true" ||
    element.isContentEditable;
  if (!isEditable) return false;

  // Fortune Sheet keeps this contenteditable focused even in selection mode.
  // The surrounding input box is raised above the grid only during real edits.
  if (element.classList?.contains("luckysheet-cell-input")) {
    const inputBox = element.closest(".luckysheet-input-box");
    return Number(inputBox?.style?.zIndex) > 0;
  }

  return true;
};

const placeCaretAtTextOffset = (editor, requestedOffset) => {
  if (!editor) return;

  const textLength = getEditorValue(editor).length;
  const offset = Math.min(Math.max(requestedOffset, 0), textLength);

  if (typeof editor.setSelectionRange === "function") {
    editor.setSelectionRange(offset, offset);
    return;
  }

  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  let traversedLength = 0;

  while (textNode) {
    const nextLength = traversedLength + textNode.textContent.length;
    if (offset <= nextLength) {
      range.setStart(textNode, offset - traversedLength);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    traversedLength = nextLength;
    textNode = walker.nextNode();
  }

  range.selectNodeContents(editor);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const getCaretTextOffset = (editor) => {
  if (typeof editor?.selectionEnd === "number") {
    return editor.selectionEnd;
  }

  const selection = window.getSelection();
  if (!editor || !selection?.rangeCount) return null;

  const caretRange = selection.getRangeAt(0);
  if (!editor.contains(caretRange.endContainer)) return null;

  const beforeCaret = caretRange.cloneRange();
  beforeCaret.selectNodeContents(editor);
  beforeCaret.setEnd(caretRange.endContainer, caretRange.endOffset);
  return beforeCaret.toString().length;
};

const replaceEditorTextRange = (
  editor,
  requestedStart,
  requestedEnd,
  replacement
) => {
  if (!editor) return null;

  const currentValue = getEditorValue(editor);
  const start = Math.min(Math.max(requestedStart, 0), currentValue.length);
  const end = Math.min(
    Math.max(requestedEnd, start),
    currentValue.length
  );
  const nextValue =
    currentValue.slice(0, start) +
    replacement +
    currentValue.slice(end);
  const caretOffset = start + replacement.length;

  if ("value" in editor) {
    editor.value = nextValue;
  } else if (nextValue.startsWith("=")) {
    // Match FortuneSheet's own formula markup before dispatching input. Its
    // caret cache uses the active formula span index plus the offset inside
    // that span; plain text makes it incorrectly save F|47 instead of F47|.
    editor.innerHTML = functionHTMLGenerate(escapeScriptTag(nextValue));
  } else {
    editor.textContent = nextValue;
  }

  // FortuneSheet reads the selection during the input event before rebuilding
  // the formula as colored spans, so the caret must be correct first.
  placeCaretAtTextOffset(editor, caretOffset);
  const inputEvent =
    typeof InputEvent === "function"
      ? new InputEvent("input", {
          bubbles: true,
          inputType: "insertText",
          data: replacement,
        })
      : new Event("input", { bubbles: true });
  editor.dispatchEvent(inputEvent);

  return { caretOffset, value: nextValue };
};

const getCellAxisBounds = (
  index,
  customSizes,
  hiddenIndexes,
  defaultSize,
  zoomRatio
) => {
  let start = 0;

  for (let currentIndex = 0; currentIndex <= index; currentIndex++) {
    const isHidden = hiddenIndexes?.[currentIndex] != null;
    const configuredSize = customSizes?.[currentIndex] || defaultSize;
    const size = isHidden
      ? 0
      : Math.round((configuredSize + 1) * zoomRatio);

    if (currentIndex === index) {
      return { start, end: start + size };
    }
    start += size;
  }

  return { start: 0, end: 0 };
};

const getCellIndexAtOffset = (
  offset,
  count,
  customSizes,
  hiddenIndexes,
  defaultSize,
  zoomRatio
) => {
  let end = 0;

  for (let index = 0; index < count; index++) {
    const isHidden = hiddenIndexes?.[index] != null;
    const configuredSize = customSizes?.[index] || defaultSize;
    end += isHidden
      ? 0
      : Math.round((configuredSize + 1) * zoomRatio);

    if (offset < end) return index;
  }

  return Math.max(0, count - 1);
};

// Function to validate if parentheses are balanced in a formula
const validateFormulaParentheses = (formula, t) => {
  if (!formula || typeof formula !== "string") return { isValid: true };

  // Check if it's a formula (starts with =)
  if (!formula.trim().startsWith("=")) return { isValid: true };

  let openCount = 0;
  let closeCount = 0;
  let inString = false;
  let stringChar = null;

  for (let i = 0; i < formula.length; i++) {
    const char = formula[i];
    const prevChar = i > 0 ? formula[i - 1] : "";

    // Handle string literals (skip parentheses inside strings)
    if ((char === '"' || char === "'") && prevChar !== "\\") {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
        stringChar = null;
      }
    }

    // Only count parentheses outside of strings
    if (!inString) {
      if (char === "(") {
        openCount++;
      } else if (char === ")") {
        closeCount++;
      }
    }
  }

  if (openCount !== closeCount) {
    const missingCount = openCount - closeCount;
    const errorMessage = t
      ? t("FormulaErrorUnclosedParenthesis", { count: missingCount })
      : `Formula error: Unclosed parenthesis. Expected ${missingCount} more closing parenthesis(es).`;

    return {
      isValid: false,
      error: errorMessage,
    };
  }

  return { isValid: true };
};

const isFormulaCompleteForArrowNavigation = (formula) => {
  const trimmed = formula?.trim?.() || "";
  if (!trimmed.startsWith("=") || trimmed === "=") return false;
  if (!validateFormulaParentheses(trimmed).isValid) return false;

  let inString = false;
  let stringChar = null;
  for (let i = 1; i < trimmed.length; i++) {
    const char = trimmed[i];
    if (char !== '"' && char !== "'") continue;

    if (!inString) {
      inString = true;
      stringChar = char;
    } else if (char === stringChar) {
      if (trimmed[i + 1] === char) {
        i++;
      } else {
        inString = false;
        stringChar = null;
      }
    }
  }

  if (inString) return false;

  const expression = trimmed.slice(1).trim();
  return !/[+\-*/^&=<>:,;.!({]$/.test(expression);
};

const transformData = (responseData) => {
  return responseData?.map((sheet) => {
    // Already in fortune-sheet format (has celldata) — pass through unchanged so
    // column widths (config.columnlen) and row heights (config.rowlen) are preserved.
    if (sheet.celldata) {
      return {
        ...sheet,
        celldata: sheet.celldata.map((cell) => ({
          ...cell,
          v: applyThousandsFormat(cell.v),
        })),
      };
    }
    // Backend format (has data 2D array): convert to fortune-sheet celldata format.
    return {
      ...sheet,
      celldata: sheet?.data?.flatMap((row, rIndex) =>
        row
          ?.map((cell, cIndex) => {
            if (cell !== null) {
              return {
                r: rIndex,
                c: cIndex,
                v: applyThousandsFormat(cell),
              };
            }
            return undefined;
          })
          ?.filter((cell) => cell !== undefined)
      ),
      calcChain: sheet?.calcChain?.map((item) => {
        const relatedCell = sheet.data[item.r][item.c];
        return {
          r: item.r,
          c: item.c,
          id: item.id,
          v: relatedCell !== null ? relatedCell : null,
        };
      }),
    };
  });
};

const SpreadsheetEditorNew = (props) => {
  const {
    docUrl,
    data,
    onBlur = () => {},
    readOnly,
    onResetComplete = () => {},
  } = props;
  const ref = useRef();
  const parentSheetRef = useRef(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // innerData is passed to Workbook once at mount and MUST NOT be updated while
  // fortune-sheet is mounted — feeding updated data back via the prop causes
  // fortune-sheet to reset its internal state mid-operation (breaks drag-and-drop).
  // setInnerData is only safe to call when fortune-sheet is unmounted (loading=true).
  const [innerData, setInnerData] = useState(() =>
    data?.value
      ? transformData(data?.value)
      : [{ name: "Sheet1", celldata: [{ r: 0, c: 0, v: null }] }]
  );
  // Tracks the latest sheet data without triggering re-renders.
  const innerDataRef = useRef(innerData);
  const [hasMounted, setHasMounted] = useState(false);
  const [formulaError, setFormulaError] = useState(null);
  // Internal clipboard for handling rich content copy/paste within the app
  const internalClipboard = useRef(null);
  const resizeTimer = useRef(null);
  const formulaReferencePickerRef = useRef(null);
  // Track previous row heights to detect changes from dragging
  const prevRowHeightsRef = useRef(null);

  const [showResetModal, setShowResetModal] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);

  const [showPasteSpecialModal, setShowPasteSpecialModal] = useState(false);
  const [pasteSpecialOptions, setPasteSpecialOptions] = useState({
    formulas: false,
    values: false,
    formats: false,
    all: false, // default nothing selected
  });

  const DEFAULT_ROW_HEIGHT = 19;
  const DEFAULT_COL_WIDTH = 62;

  const ensureCellVisible = useCallback(
    (row, column) => {
      const workbook = ref.current;
      const parentSheet = parentSheetRef.current;
      const cellArea = parentSheet?.querySelector(".fortune-cell-area");
      const verticalScrollbar = parentSheet?.querySelector(
        ".luckysheet-scrollbar-y"
      );
      const horizontalScrollbar = parentSheet?.querySelector(
        ".luckysheet-scrollbar-x"
      );
      const sheet = workbook?.getSheet?.();

      if (
        !workbook ||
        !cellArea ||
        !verticalScrollbar ||
        !horizontalScrollbar ||
        !sheet
      ) {
        return;
      }

      const zoomRatio = Number(sheet.zoomRatio) || 1;
      const config = sheet.config || {};
      const rowBounds = getCellAxisBounds(
        row,
        config.rowlen,
        config.rowhidden,
        sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
        zoomRatio
      );
      const columnBounds = getCellAxisBounds(
        column,
        config.columnlen,
        config.colhidden,
        sheet.defaultColWidth || DEFAULT_COL_WIDTH,
        zoomRatio
      );
      const scrollOptions = {};
      const scrollMargin = 20;

      if (rowBounds.start < verticalScrollbar.scrollTop) {
        scrollOptions.scrollTop = Math.max(
          0,
          rowBounds.start - scrollMargin
        );
      } else if (
        rowBounds.end >
        verticalScrollbar.scrollTop + cellArea.clientHeight - scrollMargin
      ) {
        scrollOptions.scrollTop = Math.max(
          0,
          rowBounds.end - cellArea.clientHeight + scrollMargin
        );
      }

      if (columnBounds.start < horizontalScrollbar.scrollLeft) {
        scrollOptions.scrollLeft = Math.max(
          0,
          columnBounds.start - scrollMargin
        );
      } else if (
        columnBounds.end >
        horizontalScrollbar.scrollLeft + cellArea.clientWidth - scrollMargin
      ) {
        scrollOptions.scrollLeft = Math.max(
          0,
          columnBounds.end - cellArea.clientWidth + scrollMargin
        );
      }

      if (Object.keys(scrollOptions).length > 0) {
        workbook.scroll?.(scrollOptions);
      }
    },
    [DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT]
  );

  const [showRowHeightModal, setShowRowHeightModal] = useState(false);
  const [showColWidthModal, setShowColWidthModal] = useState(false);

  const [rowHeightMode, setRowHeightMode] = useState("auto"); // "auto" | "custom"
  const [rowHeightValue, setRowHeightValue] = useState(DEFAULT_ROW_HEIGHT);

  const [colWidthMode, setColWidthMode] = useState("auto"); // "auto" | "custom"
  const [colWidthValue, setColWidthValue] = useState(DEFAULT_COL_WIDTH);

  // reset logic part 1
  const addTemporarySheet = useCallback(() => {
    const workbook = ref.current;
    if (!workbook) return;

    workbook.addSheet(); // Add new sheet immediately
    setPendingReset(true); // Mark that full reset should run next
  }, [ref]);

  // reset logic part 2
  const completeReset = useCallback(() => {
    if (!pendingReset) return; // Only run if we added a temp sheet first

    const workbook = ref.current;
    if (!workbook) {
      setPendingReset(false);
      return;
    }

    // Now the new sheet exists — safe to delete old ones
    let allSheets = workbook.getAllSheets();

    // Delete all sheets except the newest one
    for (let i = 0; i < allSheets.length - 1; i++) {
      if (allSheets[i]?.id) {
        workbook.deleteSheet({ id: allSheets[i].id });
      }
    }

    // Now only the new sheet remains
    const finalSheets = workbook.getAllSheets();

    if (finalSheets.length === 1 && finalSheets[0]?.id) {
      workbook.setSheetName("Sheet1", { id: finalSheets[0].id });
    }

    // Sync ref and persist to parent
    const syncedSheets = workbook.getAllSheets();
    innerDataRef.current = syncedSheets;

    if (data?.setValue) {
      data.setValue(syncedSheets);
    }

    setTimeout(() => {
      onResetComplete(); // This tells parent to remount after current function call is finished
    }, 0);

    // Reset flag
    setPendingReset(false);
    setShowResetModal(false);
  }, [pendingReset, ref, data, onResetComplete]);

  useEffect(() => {
    if (pendingReset && ref.current) {
      // Small delay to ensure addSheet has taken effect
      const timer = setTimeout(completeReset, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingReset, completeReset]);

  // Dynamically add custom class to the dimmer overlay of this specific Reset modal
  useEffect(() => {
    if (!showResetModal) {
      // When modal closes: remove custom class from any dimmer (cleanup)
      const dimmer = document.querySelector(
        ".ui.page.modals.dimmer.transition.visible.active.ExcelEditorResetModalOverlay"
      );
      if (dimmer) {
        dimmer.classList.remove("ExcelEditorResetModalOverlay");
      }
      return;
    }

    // When modal opens: find the active dimmer and add custom class
    const modalElement = document.getElementById("ExcelEditorResetModal");
    if (!modalElement) return;

    // Find the closest active dimmer (the overlay)
    const dimmer = modalElement.closest(
      ".ui.page.modals.dimmer.transition.visible.active"
    );
    if (dimmer && !dimmer.classList.contains("ExcelEditorResetModalOverlay")) {
      dimmer.classList.add("ExcelEditorResetModalOverlay");
    }

    // Cleanup on unmount or close
    return () => {
      if (dimmer) {
        dimmer.classList.remove("ExcelEditorResetModalOverlay");
      }
    };
  }, [showResetModal]);

  // Dynamically add custom class to the dimmer overlay of the Paste Special modal
  useEffect(() => {
    if (!showPasteSpecialModal) {
      // When modal closes: remove custom class (cleanup)
      const dimmer = document.querySelector(
        ".ui.page.modals.dimmer.transition.visible.active.ExcelEditorPasteSpecialModalOverlay"
      );
      if (dimmer) {
        dimmer.classList.remove("ExcelEditorPasteSpecialModalOverlay");
      }
      return;
    }

    // When modal opens: find the active dimmer and add custom class
    const modalElement = document.getElementById(
      "ExcelEditorPasteSpecialModal"
    );
    if (!modalElement) return;

    const dimmer = modalElement.closest(
      ".ui.page.modals.dimmer.transition.visible.active"
    );
    if (
      dimmer &&
      !dimmer.classList.contains("ExcelEditorPasteSpecialModalOverlay")
    ) {
      dimmer.classList.add("ExcelEditorPasteSpecialModalOverlay");
    }

    // Cleanup on unmount or next close
    return () => {
      if (dimmer) {
        dimmer.classList.remove("ExcelEditorPasteSpecialModalOverlay");
      }
    };
  }, [showPasteSpecialModal]);

  // Row Height Modal overlay Custom Class
  useEffect(() => {
    if (!showRowHeightModal) {
      const dimmer = document.querySelector(
        ".ui.page.modals.dimmer.transition.visible.active.ExcelEditorRowHeightModalOverlay"
      );
      if (dimmer) dimmer.classList.remove("ExcelEditorRowHeightModalOverlay");
      return;
    }

    const modal = document.getElementById("ExcelEditorRowHeightModal");
    if (!modal) return;

    const dimmer = modal.closest(
      ".ui.page.modals.dimmer.transition.visible.active"
    );
    if (
      dimmer &&
      !dimmer.classList.contains("ExcelEditorRowHeightModalOverlay")
    ) {
      dimmer.classList.add("ExcelEditorRowHeightModalOverlay");
    }

    return () => {
      if (dimmer) dimmer.classList.remove("ExcelEditorRowHeightModalOverlay");
    };
  }, [showRowHeightModal]);

  // Column width Modal overlay Custom Class
  useEffect(() => {
    if (!showColWidthModal) {
      const dimmer = document.querySelector(
        ".ui.page.modals.dimmer.transition.visible.active.ExcelEditorColWidthModalOverlay"
      );
      if (dimmer) dimmer.classList.remove("ExcelEditorColWidthModalOverlay");
      return;
    }

    const modal = document.getElementById("ExcelEditorColWidthModal");
    if (!modal) return;

    const dimmer = modal.closest(
      ".ui.page.modals.dimmer.transition.visible.active"
    );
    if (
      dimmer &&
      !dimmer.classList.contains("ExcelEditorColWidthModalOverlay")
    ) {
      dimmer.classList.add("ExcelEditorColWidthModalOverlay");
    }

    return () => {
      if (dimmer) dimmer.classList.remove("ExcelEditorColWidthModalOverlay");
    };
  }, [showColWidthModal]);

  // Force two-row toolbar + add custom class to font-size dropdown
  useEffect(() => {
    const applyTwoRowToolbar = () => {
      const header = document.querySelector(".fortune-workarea > header");
      if (!header) return;

      const toolbar = header.querySelector(".fortune-toolbar");
      if (!toolbar) return;

      // Step 1: Enable flex-wrap and some styles on the toolbar container
      toolbar.style.cssText += `
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 0px !important;
      padding: 2px 2px 3px 2px !important;
      align-items: center !important;
    `;

      // Step 2: Get all toolbar items
      const items = toolbar.children;
      if (items.length < 10) return; // Wait until all items are rendered

      // Step 3: Force line break — target the divider after "redo" 9th item (index 8)
      const breakItem = items[8]; // This should be the "|" divider after redo
      if (
        breakItem &&
        breakItem.classList.contains("fortune-toolbar-divider")
      ) {
        breakItem.style.cssText = `
          flex: 1 1 100% !important;
          min-width: 100% !important;
          height: 0px !important;
          opacity: 0 !important;
          pointer-events: none !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
        `;
      }

      // Step 4: Target font-size combo 10th item (index 9) and add custom class
      const fontSizeContainer = items[9]; // fortune-toobar-combo-container
      if (
        fontSizeContainer &&
        fontSizeContainer.classList.contains("fortune-toobar-combo-container")
      ) {
        const innerCombo = fontSizeContainer.querySelector(
          ".fortune-toolbar-combo"
        );
        if (
          innerCombo &&
          !innerCombo.classList.contains("DifrExcelEditorFontSize")
        ) {
          innerCombo.classList.add("DifrExcelEditorFontSize");
        }
      }
    };

    // Initial apply
    const timer = setTimeout(applyTwoRowToolbar, 300);

    // Re-apply on resize
    const handleResize = () => {
      clearTimeout(window.twoRowTimer);
      window.twoRowTimer = setTimeout(applyTwoRowToolbar, 100);
    };
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
      clearTimeout(window.twoRowTimer);
    };
  }, [readOnly]); // Re-run when readOnly changes

  // Auto-fit a column to its widest cell content
  const autoFitColumn = useCallback((colIndex) => {
    const workbook = ref.current;
    if (!workbook) return;

    const cells = workbook.getCellsByRange?.({
      row: [0, 299],
      column: [colIndex, colIndex],
    });
    if (!cells) return;

    const tempCanvas = document.createElement("canvas");
    const ctx2d = tempCanvas.getContext("2d");
    let maxWidth = DEFAULT_COL_WIDTH;

    cells.forEach((row) => {
      const cell = row?.[0];
      if (!cell) return;
      const text = String(cell.m ?? cell.v ?? "");
      if (!text) return;
      const fontSize = cell.fs || 10;
      const bold = cell.bl ? "bold " : "";
      ctx2d.font = `${bold}${fontSize}pt Arial`;
      const measured = ctx2d.measureText(text).width + 20; // +20 for cell padding
      if (measured > maxWidth) maxWidth = measured;
    });

    workbook.setColumnWidth?.({ [colIndex]: Math.ceil(maxWidth) });
  }, [ref]);

  // Double-click a column header to auto-fit that column to its widest content
  useEffect(() => {
    if (readOnly || !hasMounted) return;

    const handleColHeaderDblClick = (e) => {
      const workbook = ref.current;
      if (!workbook) return;

      const parentSheet = parentSheetRef.current;
      const colHeaderWrap = parentSheet?.querySelector(".fortune-col-header-wrap");
      if (!colHeaderWrap?.contains(e.target)) return;

      // Horizontal scroll offset from the main sheet canvas
      const scrollLeft =
        parentSheet?.querySelector(".fortune-sheet-container")?.scrollLeft ?? 0;

      // X relative to the col-header-wrap's left edge, then make it absolute (content coords)
      const wrapRect = colHeaderWrap.getBoundingClientRect();
      const mouseX = e.clientX - wrapRect.left;
      const absX = mouseX + scrollLeft;

      // Subtract row-header width to get position within columns (column A starts at 0)
      const rowHeaderEl = parentSheet?.querySelector(".fortune-row-header-wrap");
      const rowHeaderWidth = rowHeaderEl
        ? rowHeaderEl.getBoundingClientRect().width
        : 46;
      const contentX = absX - rowHeaderWidth;
      if (contentX < 0) return; // clicked on the row-header corner

      // Walk cumulative column widths to find which column was clicked
      const totalCols = 40;
      const colWidths =
        workbook.getColumnWidth?.(
          Array.from({ length: totalCols }, (_, i) => i)
        ) ?? {};

      let cumWidth = 0;
      let targetCol = -1;
      for (let c = 0; c < totalCols; c++) {
        cumWidth += colWidths[c] ?? DEFAULT_COL_WIDTH;
        if (contentX <= cumWidth) {
          targetCol = c;
          break;
        }
      }

      if (targetCol < 0) return;
      autoFitColumn(targetCol);
    };

    document.addEventListener("dblclick", handleColHeaderDblClick, true);
    return () =>
      document.removeEventListener("dblclick", handleColHeaderDblClick, true);
  }, [readOnly, hasMounted, autoFitColumn]);

  const refreshSheetWidth = useCallback(() => {
    if (resizeTimer.current) {
      clearTimeout(resizeTimer.current);
    }

    resizeTimer.current = setTimeout(() => {
      if (ref?.current?.resize) {
        ref.current.resize();
      } else if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("resize"));
      }
      resizeTimer.current = null;
    }, 100);
  }, []);

  // Helper function to refresh scroll after row height changes
  // This fixes the issue where mouse wheel scrolling stops working after row height changes
  const refreshScrollAfterRowHeightChange = useCallback(() => {
    setTimeout(() => {
      const workbook = ref.current;
      // Call resize to recalculate dimensions and reinitialize scroll handlers
      if (workbook && typeof workbook.resize === "function") {
        workbook.resize();
      } else {
        refreshSheetWidth();
      }

      // Force scroll container to recalculate and reinitialize wheel event handlers
      // This is needed because some browsers detach wheel listeners after DOM changes
      const sheetContainer = document.querySelector(".fortune-sheet-container");
      if (sheetContainer) {
        // Trigger a synthetic scroll event to ensure handlers are active
        // Some browsers need this to reattach wheel event listeners after DOM updates
        const scrollEvent = new Event("scroll", { bubbles: true, cancelable: true });
        sheetContainer.dispatchEvent(scrollEvent);
      }
    }, 150);
  }, [refreshSheetWidth]);

  const applyRowHeight = useCallback(() => {
    const workbook = ref.current;
    if (!workbook) return;

    const selection = workbook.getSelection();
    if (!selection || selection.length === 0) return;

    const range = selection[0];
    const start = range.row[0];
    const end = range.row[1];

    const height =
      rowHeightMode === "auto"
        ? DEFAULT_ROW_HEIGHT
        : parseInt(rowHeightValue, 10);
    if (isNaN(height) || height < 0) {
      alert(t("Please enter a valid positive number"));
      return;
    }

    const rowInfo = {};
    for (let r = start; r <= end; r++) {
      rowInfo[r] = height;
    }

    workbook.setRowHeight(rowInfo);
    setShowRowHeightModal(false);

    // Use the shared helper function to refresh scroll
    refreshScrollAfterRowHeightChange();
  }, [rowHeightMode, rowHeightValue, ref, t, refreshScrollAfterRowHeightChange]);

  const applyColWidth = useCallback(() => {
    const workbook = ref.current;
    if (!workbook) return;

    const selection = workbook.getSelection();
    if (!selection || selection.length === 0) return;

    const range = selection[0];
    const start = range.column[0];
    const end = range.column[1];

    const width =
      colWidthMode === "auto" ? DEFAULT_COL_WIDTH : parseInt(colWidthValue, 10);
    if (isNaN(width) || width < 0) {
      alert(t("Please enter a valid positive number"));
      return;
    }

    const colInfo = {};
    for (let c = start; c <= end; c++) {
      colInfo[c] = width;
    }

    workbook.setColumnWidth(colInfo);
    setShowColWidthModal(false);
  }, [colWidthMode, colWidthValue, ref, t]);

  // Auto-fit a column when its header delimiter is double-clicked.
  useEffect(() => {
    if (readOnly) return;

    const parentSheet = parentSheetRef.current;
    if (!parentSheet) return;

    const handleColumnDelimiterDoubleClick = (event) => {
      const delimiter = event.target.closest(".fortune-cols-change-size");
      if (!delimiter || !parentSheet.contains(delimiter)) return;

      const workbook = ref.current;
      const columnHeader = delimiter.closest(".fortune-col-header");
      const sheet = workbook?.getSheet?.();
      if (!workbook || !columnHeader || !sheet) return;

      event.preventDefault();
      event.stopPropagation();

      const columnCount = sheet.column || 40;
      const columnIndexes = Array.from(
        { length: columnCount },
        (_, index) => index
      );
      const columnWidths = workbook.getColumnWidth(columnIndexes);
      const headerRect = columnHeader.getBoundingClientRect();
      const boundaryX =
        event.clientX - headerRect.left + columnHeader.scrollLeft;

      let columnIndex = -1;
      let accumulatedWidth = 0;
      const zoomRatio = sheet.zoomRatio || 1;
      const hiddenColumns = sheet.config?.colhidden || {};

      for (let index = 0; index < columnCount; index++) {
        if (hiddenColumns[index]) continue;

        accumulatedWidth += Math.round(
          ((columnWidths[index] || DEFAULT_COL_WIDTH) + 1) * zoomRatio
        );
        if (boundaryX <= accumulatedWidth + 6) {
          columnIndex = index;
          break;
        }
      }

      if (columnIndex < 0) return;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) return;

      let fittedWidth = DEFAULT_COL_WIDTH;
      const rows = sheet.data || [];

      rows.forEach((row) => {
        const cell = row?.[columnIndex];
        if (!cell || (cell.mc && !cell.mc.cs)) return;

        const richText = cell.ct?.s
          ?.map((part) => part?.v || "")
          .join("");
        const displayedValue =
          richText ?? cell.m ?? cell.v ?? "";
        const lines = String(displayedValue).split(/\r?\n/);
        const fontSize = Number(cell.fs) || 11;
        const fontWeight = cell.bl ? "bold" : "normal";
        const fontStyle = cell.it ? "italic" : "normal";

        context.font = `${fontStyle} ${fontWeight} ${fontSize}pt Arial`;

        lines.forEach((line) => {
          fittedWidth = Math.max(
            fittedWidth,
            Math.ceil(context.measureText(line).width) + 12
          );
        });
      });

      workbook.setColumnWidth({ [columnIndex]: fittedWidth });
      refreshSheetWidth();
    };

    parentSheet.addEventListener(
      "dblclick",
      handleColumnDelimiterDoubleClick,
      true
    );

    return () => {
      parentSheet.removeEventListener(
        "dblclick",
        handleColumnDelimiterDoubleClick,
        true
      );
    };
  }, [readOnly, refreshSheetWidth]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (docUrl) {
      setLoading(true);
      request
        .get(docUrl)
        .then(({ data: res }) => {
          setInnerData(transformData(res));
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
    //eslint-disable-next-line
  }, []);

  // Use ResizeObserver to detect both width and height changes
  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    const parentSheet = parentSheetRef.current;
    if (!parentSheet) return;

    let rafId = null;
    let lastWidth = parentSheet.offsetWidth;
    let lastHeight = parentSheet.offsetHeight;

    const resizeObserver = new ResizeObserver((entries) => {
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Use requestAnimationFrame to defer the callback and prevent loop
      rafId = requestAnimationFrame(() => {
        const entry = entries[0];
        if (entry) {
          const { width, height } = entry.contentRect;

          if (
            Math.abs(width - lastWidth) > 1 ||
            Math.abs(height - lastHeight) > 1
          ) {
            lastWidth = width;
            lastHeight = height;
            refreshSheetWidth();
          }
        }
      });
    });

    resizeObserver.observe(parentSheet);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [hasMounted, refreshSheetWidth]);

  // Validate formulas while typing
  useEffect(() => {
    if (readOnly || !hasMounted) return;

    let inputElement = null;
    let skipBlurValidation = false; // Flag to prevent duplicate validation
    let validationTimeoutId = null; // Track blur validation timeout
    let arrowReference = null;
    let isUpdatingArrowReference = false;
    let isNormalizingFormulaDecimal = false;
    let caretRestoreFrameId = null;
    let formulaSelectionFrameId = null;
    let formulaMouseSelection = null;
    let formulaMouseStartTimeoutId = null;
    let existingCellEdit = null;

    const restoreCaretAfterFormulaRender = (
      editor,
      caretOffset,
      expectedValue
    ) => {
      placeCaretAtTextOffset(editor, caretOffset);

      if (caretRestoreFrameId) {
        cancelAnimationFrame(caretRestoreFrameId);
      }
      caretRestoreFrameId = requestAnimationFrame(() => {
        caretRestoreFrameId = null;
        if (
          document.activeElement === editor &&
          getEditorValue(editor) === expectedValue
        ) {
          placeCaretAtTextOffset(editor, caretOffset);
        }
      });
    };

    const handleInput = (event) => {
      const target = event.target;
      let currentValue = getEditorValue(target);

      // FortuneSheet only enables cell-reference picking for formulas beginning
      // with "=". Treat a standalone minus as the start of a negative formula.
      if (currentValue === "-") {
        if ("value" in target) {
          target.setRangeText?.("=", 0, 0, "end");
        } else {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(target);
          range.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(range);
          document.execCommand("insertText", false, "=");
        }
      }

      if (!isNormalizingFormulaDecimal) {
        const caretOffset = getCaretTextOffset(target);
        const normalizedFormula = normalizeFormulaDecimalLiterals(
          currentValue,
          caretOffset
        );

        if (normalizedFormula.value !== currentValue) {
          isNormalizingFormulaDecimal = true;
          let replacementResult = null;
          try {
            replacementResult = replaceEditorTextRange(
              target,
              0,
              currentValue.length,
              normalizedFormula.value
            );
          } finally {
            isNormalizingFormulaDecimal = false;
          }

          if (replacementResult) {
            currentValue = replacementResult.value;
            restoreCaretAfterFormulaRender(
              target,
              normalizedFormula.caretOffset ?? replacementResult.caretOffset,
              replacementResult.value
            );
          }
        }
      }

      if (!isUpdatingArrowReference) {
        arrowReference = null;
        clearFormulaReferenceSelection();
      }
      setFormulaError(null);
      skipBlurValidation = false; // Reset flag on input change
      // Clear any pending blur validation
      if (validationTimeoutId) {
        clearTimeout(validationTimeoutId);
        validationTimeoutId = null;
      }
    };

    const handleSpreadsheetInput = (event) => {
      const target = event.target;
      const parentSheet = parentSheetRef.current;
      const isCellEditor = target?.classList?.contains(
        "luckysheet-cell-input"
      );
      const isFormulaBar = target?.id === "luckysheet-functionbox-cell";

      if (
        !parentSheet?.contains(target) ||
        (!isCellEditor && !isFormulaBar)
      ) {
        return;
      }

      handleInput(event);
    };

    const validateFormula = (target, showAlert = false) => {
      if (!target) return { isValid: true };
      const value =
        target?.value || target?.textContent || target?.innerText || "";

      if (value && value.trim().startsWith("=")) {
        const validation = validateFormulaParentheses(value, t);
        setFormulaError(validation.isValid ? null : validation.error);
        if (showAlert && !validation.isValid) {
          alert(validation.error);
        }
        return validation;
      }
      setFormulaError(null);
      return { isValid: true };
    };

    const getActiveFormulaEditor = () => {
      const editor = document.activeElement;
      const parentSheet = parentSheetRef.current;

      if (
        !editor?.isConnected ||
        !parentSheet?.contains(editor) ||
        !isActivelyEditingSpreadsheet(editor) ||
        !getEditorValue(editor).trim().startsWith("=")
      ) {
        return null;
      }

      return editor;
    };

    const clearFormulaReferenceSelection = () => {
      if (formulaSelectionFrameId) {
        cancelAnimationFrame(formulaSelectionFrameId);
        formulaSelectionFrameId = null;
      }

      parentSheetRef.current
        ?.querySelector(".custom-formula-reference-highlight")
        ?.remove();
    };

    const showFormulaReferenceSelection = (
      anchorRow,
      anchorColumn,
      focusRow,
      focusColumn
    ) => {
      if (formulaSelectionFrameId) {
        cancelAnimationFrame(formulaSelectionFrameId);
      }

      formulaSelectionFrameId = requestAnimationFrame(() => {
        formulaSelectionFrameId = null;

        const parentSheet = parentSheetRef.current;
        const cellArea = parentSheet?.querySelector(".fortune-cell-area");
        const sheet = ref.current?.getSheet?.();
        if (!cellArea || !sheet) return;

        const startRow = Math.min(anchorRow, focusRow);
        const endRow = Math.max(anchorRow, focusRow);
        const startColumn = Math.min(anchorColumn, focusColumn);
        const endColumn = Math.max(anchorColumn, focusColumn);
        const zoomRatio = Number(sheet.zoomRatio) || 1;
        const config = sheet.config || {};
        const startRowBounds = getCellAxisBounds(
          startRow,
          config.rowlen,
          config.rowhidden,
          sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
          zoomRatio
        );
        const endRowBounds = getCellAxisBounds(
          endRow,
          config.rowlen,
          config.rowhidden,
          sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
          zoomRatio
        );
        const startColumnBounds = getCellAxisBounds(
          startColumn,
          config.columnlen,
          config.colhidden,
          sheet.defaultColWidth || DEFAULT_COL_WIDTH,
          zoomRatio
        );
        const endColumnBounds = getCellAxisBounds(
          endColumn,
          config.columnlen,
          config.colhidden,
          sheet.defaultColWidth || DEFAULT_COL_WIDTH,
          zoomRatio
        );

        let highlight = cellArea.querySelector(
          ".custom-formula-reference-highlight"
        );
        if (!highlight) {
          highlight = document.createElement("div");
          highlight.className =
            "fortune-selection-highlight fortune-formula-functionrange-highlight custom-formula-reference-highlight";
          highlight.style.pointerEvents = "none";

          ["top", "right", "bottom", "left"].forEach((direction) => {
            const border = document.createElement("div");
            border.className = `fortune-selection-copy-${direction} fortune-copy`;
            highlight.appendChild(border);
          });

          const background = document.createElement("div");
          background.className = "fortune-selection-copy-hc";
          highlight.appendChild(background);
          cellArea.appendChild(highlight);
        }

        highlight.style.left = `${startColumnBounds.start}px`;
        highlight.style.top = `${startRowBounds.start}px`;
        highlight.style.width = `${Math.max(
          0,
          endColumnBounds.end - startColumnBounds.start - 1
        )}px`;
        highlight.style.height = `${Math.max(
          0,
          endRowBounds.end - startRowBounds.start - 1
        )}px`;
      });
    };

    const updateFormulaMouseReference = (
      editor,
      anchorRow,
      anchorColumn,
      row,
      column,
      referenceStart,
      referenceEnd
    ) => {
      if (!editor.isConnected || document.activeElement !== editor) {
        return false;
      }

      const nextReference = getCellRangeReference(
        anchorRow,
        anchorColumn,
        row,
        column
      );
      editor.focus({ preventScroll: true });

      isUpdatingArrowReference = true;
      let replacementResult = null;
      try {
        replacementResult = replaceEditorTextRange(
          editor,
          referenceStart,
          referenceEnd,
          nextReference
        );
      } finally {
        isUpdatingArrowReference = false;
      }
      if (!replacementResult) return false;

      const nextReferenceEnd = replacementResult.caretOffset;
      arrowReference = {
        anchorRow,
        anchorColumn,
        row,
        column,
        text: nextReference,
        start: referenceStart,
        end: nextReferenceEnd,
        editor,
      };
      restoreCaretAfterFormulaRender(
        editor,
        nextReferenceEnd,
        replacementResult.value
      );
      ensureCellVisible(row, column);
      showFormulaReferenceSelection(
        anchorRow,
        anchorColumn,
        row,
        column
      );
      return true;
    };

    const selectFormulaReferenceCell = (row, column) => {
      const editor = getActiveFormulaEditor();
      if (!editor) return false;

      const canReplaceCurrentReference =
        arrowReference?.text && arrowReference.editor === editor;
      const extendCurrentReference = Boolean(
        formulaMouseSelection?.extendCurrentReference &&
          canReplaceCurrentReference
      );
      const anchorRow = extendCurrentReference
        ? arrowReference.anchorRow ?? arrowReference.row
        : row;
      const anchorColumn = extendCurrentReference
        ? arrowReference.anchorColumn ?? arrowReference.column
        : column;
      const referenceStart = canReplaceCurrentReference
        ? arrowReference.start
        : getCaretTextOffset(editor) ?? getEditorValue(editor).length;
      const referenceEnd = canReplaceCurrentReference
        ? arrowReference.end
        : referenceStart;

      if (formulaMouseSelection) {
        formulaMouseSelection.anchorRow = anchorRow;
        formulaMouseSelection.anchorColumn = anchorColumn;
        formulaMouseSelection.referenceStart = referenceStart;
        formulaMouseSelection.lastRow = row;
        formulaMouseSelection.lastColumn = column;
      }

      // Keep reference picking independent from Fortune Sheet's cached range
      // state. Delay the first update until its beforeCellMouseDown hook exits.
      formulaMouseStartTimeoutId = setTimeout(() => {
        formulaMouseStartTimeoutId = null;
        updateFormulaMouseReference(
          editor,
          anchorRow,
          anchorColumn,
          row,
          column,
          referenceStart,
          referenceEnd
        );
      }, 0);

      return true;
    };

    const formulaReferencePicker = {
      selectCell: selectFormulaReferenceCell,
    };
    formulaReferencePickerRef.current = formulaReferencePicker;

    const handleFormulaCellMouseDown = (event) => {
      const cellArea = event.target?.closest?.(".fortune-cell-area");
      const parentSheet = parentSheetRef.current;
      const editor = getActiveFormulaEditor();

      formulaMouseSelection = null;

      if (
        cellArea &&
        parentSheet?.contains(cellArea) &&
        editor
      ) {
        formulaMouseSelection = {
          editor,
          extendCurrentReference: event.shiftKey,
          anchorRow: null,
          anchorColumn: null,
          referenceStart: null,
          lastRow: null,
          lastColumn: null,
        };
        // Keep the contenteditable focused until beforeCellMouseDown inserts
        // or replaces the clicked cell reference.
        event.preventDefault();
      }
    };

    const getFormulaCellAtPointer = (event) => {
      const parentSheet = parentSheetRef.current;
      const cellArea = parentSheet?.querySelector(".fortune-cell-area");
      const sheet = ref.current?.getSheet?.();
      if (!cellArea || !sheet) return null;

      const rect = cellArea.getBoundingClientRect();
      const x = Math.max(
        0,
        Math.min(
          cellArea.scrollLeft + cellArea.clientWidth - 1,
          event.clientX - rect.left + cellArea.scrollLeft
        )
      );
      const y = Math.max(
        0,
        Math.min(
          cellArea.scrollTop + cellArea.clientHeight - 1,
          event.clientY - rect.top + cellArea.scrollTop
        )
      );
      const rowCount = sheet.row || 300;
      const columnCount = sheet.column || 40;
      const zoomRatio = Number(sheet.zoomRatio) || 1;
      const config = sheet.config || {};

      return {
        row: getCellIndexAtOffset(
          y,
          rowCount,
          config.rowlen,
          config.rowhidden,
          sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
          zoomRatio
        ),
        column: getCellIndexAtOffset(
          x,
          columnCount,
          config.columnlen,
          config.colhidden,
          sheet.defaultColWidth || DEFAULT_COL_WIDTH,
          zoomRatio
        ),
      };
    };

    const handleFormulaCellMouseMove = (event) => {
      const mouseSelection = formulaMouseSelection;
      if (!mouseSelection || (event.buttons & 1) !== 1) return;

      const cell = getFormulaCellAtPointer(event);
      if (
        !cell ||
        mouseSelection.anchorRow == null ||
        mouseSelection.anchorColumn == null ||
        mouseSelection.referenceStart == null ||
        (cell.row === mouseSelection.lastRow &&
          cell.column === mouseSelection.lastColumn)
      ) {
        return;
      }

      if (formulaMouseStartTimeoutId) {
        clearTimeout(formulaMouseStartTimeoutId);
        formulaMouseStartTimeoutId = null;
      }

      const currentReferenceEnd =
        arrowReference?.editor === mouseSelection.editor
          ? arrowReference.end
          : mouseSelection.referenceStart;
      if (
        updateFormulaMouseReference(
          mouseSelection.editor,
          mouseSelection.anchorRow,
          mouseSelection.anchorColumn,
          cell.row,
          cell.column,
          mouseSelection.referenceStart,
          currentReferenceEnd
        )
      ) {
        mouseSelection.lastRow = cell.row;
        mouseSelection.lastColumn = cell.column;
      }

      event.preventDefault();
    };

    const handleFormulaCellMouseUp = () => {
      formulaMouseSelection = null;
    };

    const refreshExistingCellEdit = (editor) => {
      const workbook = ref.current;
      const selection = workbook?.getSelection?.();
      const range = selection?.[selection.length - 1];
      const row = range?.row_focus ?? range?.row?.[0];
      const column = range?.column_focus ?? range?.column?.[0];
      const isSameExistingEditCell =
        existingCellEdit?.row === row && existingCellEdit?.column === column;

      if (!isSameExistingEditCell) {
        const selectedCell =
          row == null || column == null
            ? null
            : workbook?.getCellsByRange?.({
                row: [row, row],
                column: [column, column],
              })?.[0]?.[0];
        const inlineText = selectedCell?.ct?.s
          ?.map((part) => part?.v ?? "")
          .join("");
        const storedEditorValues =
          selectedCell && typeof selectedCell === "object"
            ? [selectedCell.f, selectedCell.m, selectedCell.v, inlineText]
            : [selectedCell];
        const editorValue = getEditorValue(editor).trim();
        const isReenteringStoredValue =
          editorValue !== "" &&
          storedEditorValues.some(
            (value) => value != null && String(value).trim() === editorValue
          );

        existingCellEdit = isReenteringStoredValue ? { row, column } : null;
      }

      return existingCellEdit;
    };

    const commitEditAndMove = (editor, rowOffset, columnOffset) => {
      const workbook = ref.current;
      const selection = workbook?.getSelection?.();
      const range = selection?.[0];
      const row = range?.row_focus ?? range?.row?.[0] ?? range?.row?.[1];
      const column =
        range?.column_focus ?? range?.column?.[0] ?? range?.column?.[1];

      if (!workbook || row == null || column == null) {
        editor?.blur?.();
        return false;
      }

      const sheet = workbook.getSheet?.();
      const rowCount = sheet?.row || 300;
      const columnCount = sheet?.column || 40;
      const nextRow = Math.min(Math.max(row + rowOffset, 0), rowCount - 1);
      const nextColumn = Math.min(
        Math.max(column + columnOffset, 0),
        columnCount - 1
      );
      arrowReference = null;
      clearFormulaReferenceSelection();

      const container = parentSheetRef.current?.querySelector(
        ".fortune-container"
      );
      container?.setAttribute?.("tabindex", "-1");
      if (container) container.style.outline = "none";

      // Commit through Fortune Sheet's native Enter path. Calling setCellValue
      // directly for a plain value updates only that cell and skips the
      // dependency graph, leaving formulas that reference it with stale values.
      // Native Enter uses updateCell, which recalculates those dependants and
      // also clears the active edit state.
      editor?.dispatchEvent?.(
        new KeyboardEvent("keydown", {
          key: "Enter",
          code: "Enter",
          keyCode: 13,
          which: 13,
          bubbles: true,
          cancelable: true,
        })
      );

      setTimeout(() => {
        ref.current?.setSelection?.([
          {
            row: [nextRow, nextRow],
            column: [nextColumn, nextColumn],
            row_focus: nextRow,
            column_focus: nextColumn,
          },
        ]);
        try {
          container?.focus?.({ preventScroll: true });
        } catch {
          container?.focus?.();
        }
      }, 100);

      return true;
    };

    const handleKeyDown = (e) => {
      if (!isActivelyEditingSpreadsheet(e.target)) return;
      refreshExistingCellEdit(e.target);

      const arrowOffsets = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const arrowOffset = arrowOffsets[e.key];
      const formulaValue =
        e.target?.value || e.target?.textContent || e.target?.innerText || "";
      const formulaSearch = document.getElementById(
        "luckysheet-formula-search-c"
      );
      const hasActiveFormulaItem = formulaSearch?.querySelector(
        ".luckysheet-formula-search-item-active"
      );
      const isNavigatingFormulaSearch =
        hasActiveFormulaItem && /[A-Za-z][A-Za-z0-9_.]*$/.test(formulaValue);
      const trimmedFormulaValue = formulaValue.trim();
      const isFormula = trimmedFormulaValue.startsWith("=");
      const caretOffset = getCaretTextOffset(e.target);
      const isCaretAtFormulaEnd =
        caretOffset == null || !formulaValue.slice(caretOffset).trim();
      const shouldFinishEditWithArrow =
        !isFormula ||
        (!existingCellEdit &&
          !arrowReference &&
          !isNavigatingFormulaSearch &&
          isCaretAtFormulaEnd &&
          isFormulaCompleteForArrowNavigation(trimmedFormulaValue));

      if (e.key === "Escape") {
        arrowReference = null;
        clearFormulaReferenceSelection();
      }

      if (
        arrowOffset &&
        existingCellEdit &&
        !arrowReference &&
        (!isFormula || !isNavigatingFormulaSearch)
      ) {
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        return;
      }

      if (
        arrowOffset &&
        shouldFinishEditWithArrow &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        commitEditAndMove(e.target, arrowOffset[0], arrowOffset[1]);
        return false;
      }

      if (
        arrowOffset &&
        isFormula &&
        !isNavigatingFormulaSearch &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        const workbook = ref.current;
        const selection = workbook?.getSelection?.();
        const range = selection?.[0];
        const currentRow =
          arrowReference?.row ?? range?.row_focus ?? range?.row?.[0];
        const currentColumn =
          arrowReference?.column ??
          range?.column_focus ??
          range?.column?.[0];
        if (currentRow == null || currentColumn == null) return;

        const nextRow = currentRow + arrowOffset[0];
        const nextColumn = currentColumn + arrowOffset[1];
        const sheet = workbook?.getSheet?.();
        const rowCount = sheet?.row || 300;
        const columnCount = sheet?.column || 40;

        // Always consume formula-reference arrows once navigation has started.
        // Letting an out-of-bounds arrow reach Fortune Sheet moves its internal
        // caret and leaves the custom reference unable to move back into range.
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();

        if (
          nextRow >= 0 &&
          nextRow < rowCount &&
          nextColumn >= 0 &&
          nextColumn < columnCount
        ) {
          const anchorRow = e.shiftKey
            ? arrowReference?.anchorRow ?? arrowReference?.row ?? nextRow
            : nextRow;
          const anchorColumn = e.shiftKey
            ? arrowReference?.anchorColumn ??
              arrowReference?.column ??
              nextColumn
            : nextColumn;
          const nextReference = e.shiftKey
            ? getCellRangeReference(
                anchorRow,
                anchorColumn,
                nextRow,
                nextColumn
              )
            : getCellReference(nextRow, nextColumn);
          const editor = e.target;
          editor.focus();

          const referenceStart =
            arrowReference?.start ??
            getCaretTextOffset(editor) ??
            formulaValue.length;
          const referenceEndBeforeReplacement =
            arrowReference?.end ?? referenceStart;

          isUpdatingArrowReference = true;
          let replacementResult = null;
          try {
            replacementResult = replaceEditorTextRange(
              editor,
              referenceStart,
              referenceEndBeforeReplacement,
              nextReference
            );
          } finally {
            isUpdatingArrowReference = false;
          }
          if (!replacementResult) return false;

          const referenceEnd = replacementResult.caretOffset;
          arrowReference = {
            anchorRow,
            anchorColumn,
            row: nextRow,
            column: nextColumn,
            text: nextReference,
            start: referenceStart,
            end: referenceEnd,
            editor,
          };
          restoreCaretAfterFormulaRender(
            editor,
            referenceEnd,
            replacementResult.value
          );
          ensureCellVisible(nextRow, nextColumn);
          showFormulaReferenceSelection(
            anchorRow,
            anchorColumn,
            nextRow,
            nextColumn
          );
          return false;
        }
      }

      if (e.key === "Enter" || e.key === "Tab") {
        const value =
          e.target?.value || e.target?.textContent || e.target?.innerText || "";
        const isFormula = value.trim().startsWith("=");

        // If formula dropdown is open (user selecting from autocomplete), let library handle it
        if (hasActiveFormulaItem) {
          return;
        }

        // Validate formula if present
        if (isFormula) {
          if (validationTimeoutId) {
            clearTimeout(validationTimeoutId);
            validationTimeoutId = null;
          }
          const validation = validateFormulaParentheses(value.trim(), t);
          if (!validation.isValid) {
            e.preventDefault();
            e.stopPropagation();
            setFormulaError(validation.error);
            if (validation.error) {
              alert(validation.error);
            }
            skipBlurValidation = true;
            setTimeout(() => {
              e.target?.focus();
              setTimeout(() => {
                skipBlurValidation = false;
              }, 300);
            }, 50);
            return false;
          }
        }

        // Valid cell: let library execute its default behavior
        // Enter: library's handler works when we don't prevent - it saves and moves down
        // Tab: use setSelection (dispatch fails - Tab moves focus to other UI elements)
        skipBlurValidation = false;
        arrowReference = null;
        clearFormulaReferenceSelection();

        if (e.key === "Enter") {
          // Don't prevent - let event reach library's handleGlobalEnter
          return;
        }

        // Tab: save cell, blur, exit edit mode, move to next cell
        e.preventDefault();
        e.stopPropagation();

        const workbook = ref.current;
        const selection = workbook?.getSelection?.();
        const range = selection?.[0];
        const row = range?.row?.[0] ?? range?.row?.[1];
        const col = range?.column?.[0] ?? range?.column?.[1];
        const nextCol = e.shiftKey ? (col ?? 0) - 1 : (col ?? -1) + 1;

        const canProceed =
          workbook &&
          selection?.length > 0 &&
          row != null &&
          col != null &&
          nextCol >= 0;
        if (!canProceed) {
          e.target?.blur?.();
          return;
        }

        const trimmed = (
          e.target?.value || e.target?.textContent || e.target?.innerText || ""
        ).trim();
        const isFormulaCell = trimmed.startsWith("=");

        // Build value: formulas use string + type "f"; plain text uses { f: "=\"...\"" }
        // to force updateCell path (clears edit mode). Empty → null.
        const saveValue = !trimmed
          ? null
          : isFormulaCell
            ? trimmed
            : { f: `="${trimmed.replace(/"/g, '""')}"` };
        const saveOpts = typeof saveValue === "string" ? { type: "f" } : undefined;
        workbook.setCellValue?.(row, col, saveValue, saveOpts);

        const container = parentSheetRef.current?.querySelector(
          ".fortune-container"
        );
        container?.setAttribute?.("tabindex", "-1");
        if (container) container.style.outline = "none";

        // Escape clears luckysheetCellUpdate when cell was empty
        (e.target || container)?.dispatchEvent?.(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
        );

        e.target?.blur?.();
        container?.focus?.();

        setTimeout(() => {
          workbook.setSelection?.([
            { row: [row, row], column: [nextCol, nextCol], row_focus: row, column_focus: nextCol },
          ]);
          container?.focus?.();
        }, 100);
      }
    };

    // Validate formula when input loses focus (blur event)
    const handleBlur = (e) => {
      // Clear any existing timeout
      if (validationTimeoutId) {
        clearTimeout(validationTimeoutId);
        validationTimeoutId = null;
      }

      // Skip validation if we just handled it in handleKeyDown
      if (skipBlurValidation) {
        return;
      }

      // Small delay to allow cell selection clicks to complete first
      // This allows the fortune-sheet library to handle cell reference selection
      validationTimeoutId = setTimeout(() => {
        validationTimeoutId = null;
        const currentInput = inputElement;
        if (!currentInput) return;
        
        // Skip if flag is set (validation was already handled)
        if (skipBlurValidation) return;
        
        // Check if the input is still focused (user might have clicked a cell to add reference)
        const isStillFocused = document.activeElement === currentInput;
        
        // Check if the input is still in the DOM
        const isStillInDOM = parentSheetRef.current?.contains(currentInput);

        if (!isStillFocused) {
          arrowReference = null;
          clearFormulaReferenceSelection();
        }
        
        // Only validate if the input is no longer focused and still in DOM
        // This means editing has ended (user clicked away or pressed Enter/Tab)
        if (!isStillFocused && isStillInDOM) {
          const validation = validateFormula(currentInput, false); // Don't show alert, just set error state
          if (!validation.isValid) {
            // Show error but don't prevent blur - user has finished editing
            // The error will be shown in the error banner
            setFormulaError(validation.error);
          } else {
            // Clear error if formula is valid
            setFormulaError(null);
          }
        }
      }, 150);
    };

    const attachToInput = () => {
      const activeEl = document.activeElement;
      const parentSheet = parentSheetRef.current;

      if (!parentSheet || !activeEl || !parentSheet.contains(activeEl)) return;

      const isEditable = isActivelyEditingSpreadsheet(activeEl);

      if (!isEditable) {
        if (inputElement) {
          inputElement.removeEventListener("keydown", handleKeyDown, true);
          inputElement.removeEventListener("blur", handleBlur, true);
          inputElement = null;
        }
        existingCellEdit = null;
        return;
      }

      refreshExistingCellEdit(activeEl);

      if (activeEl !== inputElement) {
        // Remove old listeners
        if (inputElement) {
          inputElement.removeEventListener("keydown", handleKeyDown, true);
          inputElement.removeEventListener("blur", handleBlur, true);
        }

        // Attach to new input
        inputElement = activeEl;
        inputElement.addEventListener("keydown", handleKeyDown, true);
        inputElement.addEventListener("blur", handleBlur, true);
      }
    };

    // Check for active input periodically
    const intervalId = setInterval(attachToInput, 100);
    document.addEventListener("input", handleSpreadsheetInput, true);
    document.addEventListener("focusin", attachToInput, true);
    document.addEventListener("mousedown", handleFormulaCellMouseDown, true);
    document.addEventListener("mousemove", handleFormulaCellMouseMove, true);
    document.addEventListener("mouseup", handleFormulaCellMouseUp, true);

    return () => {
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyDown, true);
        inputElement.removeEventListener("blur", handleBlur, true);
      }
      if (validationTimeoutId) {
        clearTimeout(validationTimeoutId);
        validationTimeoutId = null;
      }
      if (formulaMouseStartTimeoutId) {
        clearTimeout(formulaMouseStartTimeoutId);
        formulaMouseStartTimeoutId = null;
      }
      if (caretRestoreFrameId) {
        cancelAnimationFrame(caretRestoreFrameId);
      }
      clearFormulaReferenceSelection();
      if (formulaReferencePickerRef.current === formulaReferencePicker) {
        formulaReferencePickerRef.current = null;
      }
      clearInterval(intervalId);
      document.removeEventListener("input", handleSpreadsheetInput, true);
      document.removeEventListener("focusin", attachToInput, true);
      document.removeEventListener(
        "mousedown",
        handleFormulaCellMouseDown,
        true
      );
      document.removeEventListener(
        "mousemove",
        handleFormulaCellMouseMove,
        true
      );
      document.removeEventListener(
        "mouseup",
        handleFormulaCellMouseUp,
        true
      );
    };
  }, [readOnly, hasMounted, t, ensureCellVisible]);

  // Handle Tab when NOT in edit mode (e.g. after our Tab-in-edit handler moved selection)
  // so subsequent Tab presses move to next cell instead of other UI elements
  useEffect(() => {
    if (readOnly || !hasMounted) return;

    const handleContainerTab = (e) => {
      if (e.key !== "Tab") return;

      const parentSheet = parentSheetRef.current;
      if (!parentSheet?.contains(e.target)) return;

      if (isActivelyEditingSpreadsheet(document.activeElement)) return;

      const workbook = ref.current;
      const selection = workbook?.getSelection?.();
      if (!workbook || !selection || selection.length === 0) return;

      const range = selection[0];
      const row = range?.row?.[0] ?? range?.row?.[1];
      const col = range?.column?.[0] ?? range?.column?.[1];
      if (row == null || col == null) return;

      const nextCol = e.shiftKey ? col - 1 : col + 1;
      if (nextCol < 0) return;

      e.preventDefault();
      e.stopPropagation();

      workbook.setSelection?.([
        {
          row: [row, row],
          column: [nextCol, nextCol],
          row_focus: row,
          column_focus: nextCol,
        },
      ]);

      const container = parentSheet.querySelector(".fortune-container");
      if (container) container.style.outline = "none";
      container?.focus?.();
    };

    document.addEventListener("keydown", handleContainerTab, true);
    return () => document.removeEventListener("keydown", handleContainerTab, true);
  }, [readOnly, hasMounted]);

  // Fortune Sheet normally focuses its hidden cell editor after a cell click.
  // In some embedding/layout combinations the browser leaves focus on <body>,
  // which makes document-level shortcuts look as though they came from outside
  // this editor. Give the workbook a reliable focus fallback after cell clicks.
  useEffect(() => {
    if (!hasMounted) return;

    const parentSheet = parentSheetRef.current;
    if (!parentSheet) return;

    let focusFrameId = null;
    const handleCellAreaMouseDown = (event) => {
      const cellArea = event.target?.closest?.(".fortune-cell-area");
      if (!cellArea || !parentSheet.contains(cellArea)) return;

      if (focusFrameId) cancelAnimationFrame(focusFrameId);
      focusFrameId = requestAnimationFrame(() => {
        focusFrameId = null;
        if (
          !parentSheet.isConnected ||
          parentSheet.contains(document.activeElement)
        ) {
          return;
        }

        const container = parentSheet.querySelector(".fortune-container");
        if (!container) return;

        container.setAttribute("tabindex", "-1");
        container.style.outline = "none";
        try {
          container.focus({ preventScroll: true });
        } catch {
          container.focus();
        }
      });
    };

    parentSheet.addEventListener("mousedown", handleCellAreaMouseDown, true);
    return () => {
      parentSheet.removeEventListener(
        "mousedown",
        handleCellAreaMouseDown,
        true
      );
      if (focusFrameId) cancelAnimationFrame(focusFrameId);
    };
  }, [hasMounted, loading]);

  // Jump to the sheet edge with Ctrl/Command + Arrow.
  useEffect(() => {
    if (!hasMounted) return;

    const arrowDirections = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };

    const handleCtrlArrow = (event) => {
      const direction = arrowDirections[event.key];
      if (
        !direction ||
        (!event.ctrlKey && !event.metaKey) ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }

      const parentSheet = parentSheetRef.current;
      if (!parentSheet) return;

      const eventPath = event.composedPath?.() || [];
      const targetIsInsideSheet =
        eventPath.includes(parentSheet) || parentSheet.contains(event.target);
      if (!targetIsInsideSheet) return;

      if (isActivelyEditingSpreadsheet(document.activeElement)) return;

      const workbook = ref.current;
      const selection = workbook?.getSelection?.();
      const currentRange = selection?.[selection.length - 1];
      if (!workbook || !currentRange) return;

      const currentRow = currentRange.row_focus ?? currentRange.row?.[0];
      const currentColumn =
        currentRange.column_focus ?? currentRange.column?.[0];
      if (currentRow == null || currentColumn == null) return;

      const sheet = workbook.getSheet?.();
      const rowCount = sheet?.row || 300;
      const columnCount = sheet?.column || 40;
      const [rowStep, columnStep] = direction;
      let targetRow = currentRow;
      let targetColumn = currentColumn;

      if (rowStep < 0) targetRow = 0;
      if (rowStep > 0) targetRow = Math.max(rowCount - 1, 0);
      if (columnStep < 0) targetColumn = 0;
      if (columnStep > 0) targetColumn = Math.max(columnCount - 1, 0);

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      workbook.setSelection?.([
        {
          row: [targetRow, targetRow],
          column: [targetColumn, targetColumn],
          row_focus: targetRow,
          column_focus: targetColumn,
        },
      ]);
      ensureCellVisible(targetRow, targetColumn);
      parentSheet.querySelector(".fortune-container")?.focus?.();
    };

    document.addEventListener("keydown", handleCtrlArrow, true);
    return () =>
      document.removeEventListener("keydown", handleCtrlArrow, true);
  }, [hasMounted, ensureCellVisible]);

  // Move the active cell and viewport by one screen with Page Up/Page Down.
  useEffect(() => {
    if (!hasMounted) return;

    const pageDirections = {
      PageUp: -1,
      PageDown: 1,
    };

    const handlePageNavigation = (event) => {
      const pageDirection = pageDirections[event.key];
      if (
        !pageDirection ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }

      const parentSheet = parentSheetRef.current;
      if (!parentSheet) return;

      const eventPath = event.composedPath?.() || [];
      const targetIsInsideSheet =
        eventPath.includes(parentSheet) || parentSheet.contains(event.target);
      if (!targetIsInsideSheet) return;

      if (isActivelyEditingSpreadsheet(document.activeElement)) return;

      const workbook = ref.current;
      const selection = workbook?.getSelection?.();
      const currentRange = selection?.[selection.length - 1];
      if (!workbook || !currentRange) return;

      const currentRow = currentRange.row_focus ?? currentRange.row?.[0];
      const currentColumn =
        currentRange.column_focus ?? currentRange.column?.[0];
      if (currentRow == null || currentColumn == null) return;

      const cellArea = parentSheet.querySelector(".fortune-cell-area");
      const verticalScrollbar = parentSheet.querySelector(
        ".luckysheet-scrollbar-y"
      );
      const sheet = workbook.getSheet?.();
      if (!cellArea || !verticalScrollbar || !sheet) return;

      const viewportHeight = cellArea.clientHeight;
      if (viewportHeight <= 0) return;

      const rowCount = sheet.row || 300;
      const zoomRatio = Number(sheet.zoomRatio) || 1;
      const config = sheet.config || {};
      const currentRowBounds = getCellAxisBounds(
        currentRow,
        config.rowlen,
        config.rowhidden,
        sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
        zoomRatio
      );
      const targetOffset = Math.max(
        0,
        currentRowBounds.start + pageDirection * viewportHeight
      );
      let targetRow = getCellIndexAtOffset(
        targetOffset,
        rowCount,
        config.rowlen,
        config.rowhidden,
        sheet.defaultRowHeight || DEFAULT_ROW_HEIGHT,
        zoomRatio
      );

      // Ensure the active cell advances even when a single row is taller than
      // the viewport.
      if (pageDirection > 0 && targetRow <= currentRow) {
        targetRow = Math.min(currentRow + 1, rowCount - 1);
      }
      if (pageDirection < 0 && targetRow >= currentRow) {
        targetRow = Math.max(currentRow - 1, 0);
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      workbook.setSelection?.([
        {
          row: [targetRow, targetRow],
          column: [currentColumn, currentColumn],
          row_focus: targetRow,
          column_focus: currentColumn,
        },
      ]);
      workbook.scroll?.({
        scrollTop: Math.max(
          0,
          verticalScrollbar.scrollTop + pageDirection * viewportHeight
        ),
      });
      ensureCellVisible(targetRow, currentColumn);
      parentSheet.querySelector(".fortune-container")?.focus?.();
    };

    document.addEventListener("keydown", handlePageNavigation, true);
    return () =>
      document.removeEventListener("keydown", handlePageNavigation, true);
  }, [DEFAULT_ROW_HEIGHT, ensureCellVisible, hasMounted]);

  const handleChange = useCallback((newData) => {
    // Detect row height changes from dragging row separators
    if (newData && newData.length > 0) {
      const currentSheet = newData[0];
      const prevSheet = innerDataRef.current?.[0];

      if (currentSheet?.row && prevSheet?.row) {
        const currentRowHeights = JSON.stringify(currentSheet.row);
        const prevRowHeights = JSON.stringify(prevSheet.row || {});

        if (currentRowHeights !== prevRowHeights && prevRowHeightsRef.current !== currentRowHeights) {
          prevRowHeightsRef.current = currentRowHeights;
          refreshScrollAfterRowHeightChange();
        }
      } else if (currentSheet?.row && !prevSheet?.row) {
        prevRowHeightsRef.current = JSON.stringify(currentSheet.row);
        refreshScrollAfterRowHeightChange();
      }
    }

    innerDataRef.current = newData;
    if (data?.setValue) {
      data.setValue(newData);
    }
  }, [data, refreshScrollAfterRowHeightChange]);

  // Menu handlers
  const handleMenuClick = useCallback(
    (e, { name }) => {
      // Close menu immediately
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Safe access to ref.current
      const workbookInstance = ref.current;
      const container = document.getElementsByClassName(
        "fortune-sheet-container"
      )?.[0];

      // Helper to restore focus to the sheet before executing commands
      // This is crucial for execCommand or key events to be picked up by the library
      const focusSheet = () => {
        if (container) {
          // Find the internal canvas or editable element if possible, or focus container
          // FortuneSheet usually creates a container with class 'fortune-sheet-container' or similar
          // We will try to focus the first focusable element or the container itself
          // Setting tabindex allows div to be focused
          // if (!container.getAttribute('tabindex')) {
          //     container.setAttribute('tabindex', '-1');
          // }
          container.focus();
        }
      };

      if (name === "undo") {
        if (workbookInstance && workbookInstance.handleUndo) {
          workbookInstance.handleUndo();
        }
      } else if (name === "redo") {
        if (workbookInstance && workbookInstance.handleRedo) {
          workbookInstance.handleRedo();
        }
      } else if (name === "cut") {
        const selection = workbookInstance.getSelection();
        if (selection && selection.length > 0) {
          const range = selection[0];
          const cells = workbookInstance.getCellsByRange(range);

          // Store full cell objects in internal clipboard for rich paste
          internalClipboard.current = {
            cells: JSON.parse(JSON.stringify(cells)), // Deep copy
            rowCount: cells.length,
            colCount: cells[0]?.length || 0,
          };

          const text = cells
            .map((row) =>
              row
                .map((cell) => {
                  if (!cell) return "";
                  return cell.v !== undefined && cell.v !== null
                    ? cell.v
                    : cell.m || "";
                })
                .join("\t")
            )
            .join("\n");

          navigator.clipboard
            .writeText(text)
            .then(() => {
              // Clear content after copy using batch update
              const emptyData = cells.map((row) => row.map(() => null));
              workbookInstance.setCellValuesByRange(emptyData, range);
            })
            .catch((err) => {
              console.error("Cut failed:", err);
              if (
                err.name === "NotAllowedError" ||
                err.name === "SecurityError"
              ) {
                alert(
                  t(
                    "Clipboard write permission denied. Please allow clipboard access."
                  )
                );
              } else {
                alert(t("Cut failed: ") + err.message);
              }
            });
        }
      } else if (name === "copy") {
        const selection = workbookInstance.getSelection();
        if (selection && selection.length > 0) {
          const cells = workbookInstance.getCellsByRange(selection[0]);

          // Store full cell objects in internal clipboard
          internalClipboard.current = {
            cells: JSON.parse(JSON.stringify(cells)),
            rowCount: cells.length,
            colCount: cells[0]?.length || 0,
          };

          const text = cells
            .map((row) =>
              row
                .map((cell) => {
                  if (!cell) return "";
                  return cell.v !== undefined && cell.v !== null
                    ? cell.v
                    : cell.m || "";
                })
                .join("\t")
            )
            .join("\n");

          navigator.clipboard
            .writeText(text)
            .then(() => {
              // Success
            })
            .catch((err) => {
              console.error("Copy failed:", err);
              if (
                err.name === "NotAllowedError" ||
                err.name === "SecurityError"
              ) {
                alert(
                  t(
                    "Clipboard write permission denied. Please allow clipboard access."
                  )
                );
              } else {
                alert(t("Copy failed: ") + err.message);
              }
            });
        }
      } else if (name === "paste") {
        navigator.clipboard
          .readText()
          .then((text) => {
            const selection = workbookInstance.getSelection();
            if (selection && selection.length > 0) {
              const startRow = selection[0].row[0];
              const startCol = selection[0].column[0];

              // Heuristic: If system clipboard text matches internal clipboard text representation,
              // use internal clipboard (rich content). Otherwise use text.
              let useInternal = false;
              if (internalClipboard.current && text) {
                const internalText = internalClipboard.current.cells
                  .map((row) =>
                    row
                      .map((cell) => {
                        if (!cell) return "";
                        return cell.v !== undefined && cell.v !== null
                          ? cell.v
                          : cell.m || "";
                      })
                      .join("\t")
                  )
                  .join("\n");

                // Check if similar (ignoring some whitespace diffs or just direct match)
                if (
                  internalText === text ||
                  internalText.trim() === text.trim()
                ) {
                  useInternal = true;
                }
              }

              if (useInternal && internalClipboard.current) {
                // Paste rich content
                const { cells, rowCount, colCount } = internalClipboard.current;
                const endRow = startRow + rowCount - 1;
                const endCol = startCol + colCount - 1;

                const range = {
                  row: [startRow, endRow],
                  column: [startCol, endCol],
                };
                // Assuming setCellValuesByRange accepts full Cell objects which include styles/formulas
                workbookInstance.setCellValuesByRange(cells, range);
              } else {
                // Paste text (fallback or external content)
                if (!text) return;
                const rows = text.split(/\r\n|\n|\r/);
                if (rows[rows.length - 1] === "") rows.pop();
                const data = rows.map((row) => row.split("\t"));

                if (data.length === 0) return;

                const endRow = startRow + data.length - 1;
                const endCol = startCol + data[0].length - 1;

                const range = {
                  row: [startRow, endRow],
                  column: [startCol, endCol],
                };

                workbookInstance.setCellValuesByRange(data, range);
              }
            }
          })
          .catch((err) => {
            console.error("Paste failed:", err);
            alert(t("Please allow clipboard access or use Ctrl+V"));
          });
      } else if (name === "paste-special") {
        if (!workbookInstance) return;

        if (!internalClipboard.current) {
          alert(t("Nothing to paste. Please copy or cut first."));
          return;
        }

        const selection = workbookInstance.getSelection();
        if (!selection || selection.length === 0) {
          alert(t("Please select the target cell or range first."));
          return;
        }

        setShowPasteSpecialModal(true);
      } else if (name === "copy-format") {
        if (!workbookInstance) return;

        const selection = workbookInstance.getSelection();
        if (!selection || selection.length === 0) {
          alert(
            t(
              "Please select a cell or range with the format you want to copy first"
            )
          );
          return;
        }

        // Find the toolbar and its items (same pattern as your two-row effect)
        const header = document.querySelector(".fortune-workarea > header");
        if (!header) {
          alert(t("Toolbar not ready yet. Try again."));
          return;
        }

        const toolbar = header.querySelector(".fortune-toolbar");
        if (!toolbar) return;

        const items = toolbar.children;
        if (items.length < 6) {
          alert(t("Toolbar items not fully loaded yet."));
          return;
        }

        // Format Painter is the 6th item (index 5)
        const formatPainterButton = items[5];

        // check it's really the format painter
        if (
          formatPainterButton &&
          (formatPainterButton.getAttribute("data-tips") === "Format-Painter" ||
            formatPainterButton.getAttribute("aria-label") === "Format-Painter")
        ) {
          formatPainterButton.click();
        } else {
          alert(t("Could not find Format Painter button."));
        }
      } else if (name === "find") {
        focusSheet();
        container.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "f",
            code: "KeyF",
            ctrlKey: true,
            bubbles: true,
            cancelable: true,
          })
        );
      } else if (name === "replace") {
        focusSheet();
        container.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "h",
            code: "KeyH",
            ctrlKey: true,
            bubbles: true,
            cancelable: true,
          })
        );
      } else if (name === "select-all") {
        focusSheet();
        container.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "a",
            code: "KeyA",
            ctrlKey: true,
            bubbles: true,
            cancelable: true,
          })
        );
      } else if (name === "row-height") {
        const selections = workbookInstance?.getSelection();
        if (!selections || selections.length === 0 || !selections[0].row) {
          alert(t("Please select one or more rows first"));
          return;
        }

        // Fetch current row heights for the selected range
        const range = selections[0];
        const selectedRows = [];
        for (let r = range.row[0]; r <= range.row[1]; r++) {
          selectedRows.push(r);
        }
        const currentHeights = workbookInstance.getRowHeight(selectedRows);

        // Check if all selected rows have the default height
        const allDefault = selectedRows.every(
          (r) => currentHeights[r] === DEFAULT_ROW_HEIGHT
        );

        if (allDefault) {
          setRowHeightMode("auto");
          setRowHeightValue(DEFAULT_ROW_HEIGHT);
        } else {
          // Use the height of the first row in the selection
          const firstRowHeight =
            currentHeights[selectedRows[0]] || DEFAULT_ROW_HEIGHT;
          setRowHeightMode("custom");
          setRowHeightValue(firstRowHeight);
        }

        setShowRowHeightModal(true);
      } else if (name === "col-width") {
        const selections = workbookInstance?.getSelection();
        if (!selections || selections.length === 0 || !selections[0].column) {
          alert(t("Please select one or more columns first"));
          return;
        }

        // Fetch current column widths for the selected range
        const range = selections[0];
        const selectedCols = [];
        for (let c = range.column[0]; c <= range.column[1]; c++) {
          selectedCols.push(c);
        }
        const currentWidths = workbookInstance.getColumnWidth(selectedCols);

        // Check if all selected columns have the default width
        const allDefault = selectedCols.every(
          (c) => currentWidths[c] === DEFAULT_COL_WIDTH
        );

        if (allDefault) {
          setColWidthMode("auto");
          setColWidthValue(DEFAULT_COL_WIDTH);
        } else {
          // Use the width of the first column in the selection
          const firstColWidth =
            currentWidths[selectedCols[0]] || DEFAULT_COL_WIDTH;
          setColWidthMode("custom");
          setColWidthValue(firstColWidth);
        }

        setShowColWidthModal(true);
      } else if (name === "bold") {
        container.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "b",
            code: "KeyB",
            ctrlKey: true,
            bubbles: true,
            cancelable: true,
          })
        );
      } else if (name === "italic") {
        const header = document.querySelector(".fortune-workarea > header");
        if (!header) {
          alert(t("Toolbar not ready yet. Try again."));
          return;
        }

        const toolbar = header.querySelector(".fortune-toolbar");
        if (!toolbar) return;

        const items = toolbar.children;
        if (items.length < 13) {
          alert(t("Toolbar items not fully loaded yet."));
          return;
        }

        // Italic button is the 13th child (index 12)
        const italicButton = items[12];

        // Safety check
        if (
          italicButton &&
          (italicButton.getAttribute("data-tips") === "Italic (Ctrl+I)" ||
            italicButton.getAttribute("aria-label") === "Italic (Ctrl+I)") // fallback if needed
        ) {
          italicButton.click();
        } else {
          alert(t("Could not find Italic button."));
        }
      } else if (name === "underline") {
        const header = document.querySelector(".fortune-workarea > header");
        if (!header) {
          alert(t("Toolbar not ready yet. Try again."));
          return;
        }

        const toolbar = header.querySelector(".fortune-toolbar");
        if (!toolbar) return;

        const items = toolbar.children;
        if (items.length < 14) {
          alert(t("Toolbar items not fully loaded yet."));
          return;
        }

        // Underline button is the 14th child (index 13)
        const underlineButton = items[13];

        if (
          underlineButton &&
          (underlineButton.getAttribute("data-tips") === "Underline" ||
            underlineButton.getAttribute("aria-label") === "Underline")
        ) {
          underlineButton.click();
        } else {
          alert(t("Could not find Underline button."));
        }
      } else if (name === "number-format") {
        const selection = workbookInstance?.getSelection?.();
        if (!selection || selection.length === 0) return;
        const range = selection[0];
        const startRow = range.row[0];
        const endRow = range.row[1];
        const startCol = range.column[0];
        const endCol = range.column[1];
        const firstCell = workbookInstance?.getCellsByRange?.({
          row: [startRow, startRow],
          column: [startCol, startCol],
        })?.[0]?.[0];
        const isAlreadyFormatted =
          firstCell?.ct?.t === "n" && firstCell?.ct?.fa !== "General";
        const newCt = isAlreadyFormatted
          ? { fa: "General", t: "g" }
          : { fa: "#,##0.00", t: "n" };
        for (let r = startRow; r <= endRow; r++) {
          for (let c = startCol; c <= endCol; c++) {
            workbookInstance?.setCellFormat?.(r, c, "ct", newCt);
          }
        }
      }
    },
    [ref, internalClipboard, t]
  );

  const handlePasteSpecialOK = useCallback(() => {
    setShowPasteSpecialModal(false);

    const workbook = ref.current;
    if (!workbook) return;

    const selection = workbook.getSelection();
    if (!selection || selection.length === 0) return;

    // If "All" is checked, use full paste
    if (pasteSpecialOptions.all) {
      handleMenuClick(null, { name: "paste" });
    } else if (
      pasteSpecialOptions.formulas ||
      pasteSpecialOptions.values ||
      pasteSpecialOptions.formats
    ) {
      // Manual paste for selected parts
      if (!internalClipboard.current) {
        alert(t("Nothing to paste (rich clipboard empty)."));
        return;
      }

      const startRow = selection[0].row[0];
      const startCol = selection[0].column[0];
      const { cells, rowCount, colCount } = internalClipboard.current;

      const formatKeys = [
        "ff",
        "fs",
        "fc",
        "bg",
        "bl",
        "it",
        "un",
        "cl",
        "ht",
        "vt",
        "tb",
        "tr",
        "ct",
      ];

      for (let r = 0; r < rowCount; r++) {
        for (let c = 0; c < colCount; c++) {
          const targetR = startRow + r;
          const targetC = startCol + c;
          const sourceCell = cells[r]?.[c];
          if (!sourceCell) continue;

          // Paste Formulas if checked
          if (pasteSpecialOptions.formulas && sourceCell.f) {
            workbook.setCellValue(targetR, targetC, sourceCell.f, {
              type: "f",
            });
          }

          // Paste Values if checked
          if (pasteSpecialOptions.values) {
            const value = sourceCell.m ?? sourceCell.v;
            if (value !== undefined) {
              workbook.setCellValue(targetR, targetC, value, { type: "v" });
            }
          }

          // Paste Formats if checked
          if (pasteSpecialOptions.formats) {
            formatKeys.forEach((attr) => {
              if (sourceCell[attr] !== undefined && sourceCell[attr] !== null) {
                workbook.setCellFormat(targetR, targetC, attr, sourceCell[attr]);
              }
            });
          }
        }
      }
    }
    // If nothing checked, do nothing
  }, [ref, handleMenuClick, t, pasteSpecialOptions]);

  if (loading) return <Segment basic loading />;

  // Custom items for toolbar to match image (Reset, Cut, Copy, Paste)
  const customToolbarItems = readOnly
    ? []
    : [
        {
          key: "reset",
          tooltip: t("customExcelToolbarItems.reset"),
          onClick: () => setShowResetModal(true),
          icon: <Icon name="file alternate outline" />,
        },
        {
          key: "cut",
          tooltip: t("customExcelToolbarItems.cut"),
          onClick: () => handleMenuClick(null, { name: "cut" }),
          icon: <Icon name="cut" />,
        },
        {
          key: "copy",
          tooltip: t("customExcelToolbarItems.copy"),
          onClick: () => handleMenuClick(null, { name: "copy" }),
          icon: <Icon name="copy" />,
        },
        {
          key: "paste",
          tooltip: t("customExcelToolbarItems.paste"),
          onClick: () => handleMenuClick(null, { name: "paste" }),
          icon: <Icon name="paste" />,
        },
        {
          key: "number-format",
          tooltip: t("customExcelToolbarItems.numberFormat"),
          onClick: () => handleMenuClick(null, { name: "number-format" }),
          icon: <Icon name="hashtag" />,
        },
      ];

  // Standard items to match image
  const toolbarItems = [
    "format-painter",
    "undo",
    "redo",
    "|",
    "font-size",
    "|",
    "bold",
    "italic",
    "underline",
    "font-color",
    "background",
    "|",
    "horizontal-align",
    "|",
    "number-decrease",
    "number-increase",
    "text-wrap",
    "currency-format",
    "percentage-format",
    "format",
  ];

  return (
    <div
      id="parentSheet"
      ref={parentSheetRef}
      className="difrExcelEditor"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Custom Menu Bar Matching Image 2 & 3 */}
      <Menu
        attached="top"
        size="small"
        style={{
          border: "none",
          boxShadow: "none",
          background: "#F1F1F1",
          minHeight: "auto",
          height: "33px",
          zIndex: 1000,
        }}
      >
        <Dropdown
          item
          text={t("excelMenu.edit.title")}
          style={{ zIndex: 9000 }}
        >
          <Dropdown.Menu style={{ zIndex: 9000 }}>
            <Dropdown.Item
              text={t("excelMenu.edit.undo")}
              description="Ctrl+Z"
              onClick={() => handleMenuClick(null, { name: "undo" })}
            />
            <Dropdown.Item
              text={t("excelMenu.edit.redo")}
              description="Ctrl+Y"
              onClick={() => handleMenuClick(null, { name: "redo" })}
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text={t("excelMenu.edit.cut")}
              description="Ctrl+X"
              onClick={() => handleMenuClick(null, { name: "cut" })}
            />
            <Dropdown.Item
              text={t("excelMenu.edit.copy")}
              description="Ctrl+C"
              onClick={() => handleMenuClick(null, { name: "copy" })}
            />
            <Dropdown.Item
              text={t("excelMenu.edit.paste")}
              description="Ctrl+V"
              onClick={() => handleMenuClick(null, { name: "paste" })}
            />
            <Dropdown.Item
              text={t("excelMenu.edit.pasteSpecial")}
              onClick={() => handleMenuClick(null, { name: "paste-special" })}
              className="ArExcelDropdownItem"
            />
            <Dropdown.Item
              text={t("excelMenu.edit.copyFormat")}
              onClick={() => handleMenuClick(null, { name: "copy-format" })}
              className="ArExcelDropdownItem"
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text={t("excelMenu.edit.find")}
              description="Ctrl+F"
              onClick={() => handleMenuClick(null, { name: "find" })}
            />
            <Dropdown.Item
              text={t("excelMenu.edit.replace")}
              description="Ctrl+H"
              onClick={() => handleMenuClick(null, { name: "replace" })}
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text={t("excelMenu.edit.selectAll")}
              description="Ctrl+A"
              onClick={() => handleMenuClick(null, { name: "select-all" })}
            />
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown
          item
          text={t("excelMenu.format.title")}
          style={{ zIndex: 9000 }}
        >
          <Dropdown.Menu style={{ zIndex: 9000 }}>
            <Dropdown.Item
              description="Ctrl+B"
              text={t("excelMenu.format.bold")}
              onClick={() => handleMenuClick(null, { name: "bold" })}
            />
            <Dropdown.Item
              description=""
              text={t("excelMenu.format.italic")}
              onClick={() => handleMenuClick(null, { name: "italic" })}
            />
            <Dropdown.Item
              description=""
              text={t("excelMenu.format.underline")}
              onClick={() => handleMenuClick(null, { name: "underline" })}
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text={t("excelMenu.format.rowHeight")}
              onClick={() => handleMenuClick(null, { name: "row-height" })}
              className="ArExcelDropdownItem"
            />
            <Dropdown.Item
              text={t("excelMenu.format.columnWidth")}
              onClick={() => handleMenuClick(null, { name: "col-width" })}
              className="ArExcelDropdownItem"
            />
            <Dropdown.Divider />
            <Dropdown.Item
              text={t("excelMenu.format.numberFormat")}
              onClick={() => handleMenuClick(null, { name: "number-format" })}
              className="ArExcelDropdownItem"
            />
          </Dropdown.Menu>
        </Dropdown>
      </Menu>

      {/* Workbook with Toolbar */}
      <div id="excel-container" style={{ flex: 1, position: "relative" }}>
        {formulaError && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: "#ffebee",
              color: "#c62828",
              padding: "8px 16px",
              fontSize: "14px",
              zIndex: 1000,
              borderBottom: "2px solid #c62828",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Icon name="warning circle" /> {formulaError}
          </div>
        )}

        {/* Reset Confirmation Modal */}
        <Modal
          id="ExcelEditorResetModal"
          open={showResetModal}
          onClose={() => setShowResetModal(false)}
          size="small"
        >
          <Modal.Header>{t("ResetPopup.reset")}</Modal.Header>
          <Modal.Content>
            <p style={{ lineHeight: "22px", margin: 0 }}>
              {t("ResetPopup.Message")}
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() => {
                addTemporarySheet(); // Step 1: Add new sheet
                // completeReset will run automatically via useEffect
              }}
              content={t("ResetPopup.yes")}
            />
            <Button
              onClick={() => setShowResetModal(false)}
              content={t("ResetPopup.no")}
            />
          </Modal.Actions>
        </Modal>

        {/* Paste Special Modal */}
        <Modal
          id="ExcelEditorPasteSpecialModal"
          open={showPasteSpecialModal}
          onClose={() => {
            setShowPasteSpecialModal(false);
            // reset on close
            // setPasteSpecialOptions({ formulas: false, values: false, formats: false, all: false });
          }}
          size="small"
        >
          <Modal.Header>{t("excelModals.pasteSpecial.title")}</Modal.Header>
          <Modal.Content>
            <div className="tw-flex tw-flex-row tw-justify-between">
              <div className="options-container tw-relative">
                <div className="title tw-fixed tw-top-[37px] tw-bg-[#f1f1f1] tw-px-[2px] tw-mt-[1px] tw-text-black tw-font-normal tw-leading-[16px]">
                  {t("excelModals.pasteSpecial.pasteLabel")}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <Checkbox
                    label={t("excelModals.pasteSpecial.formulas")}
                    checked={pasteSpecialOptions.formulas}
                    onChange={(e, { checked }) => {
                      setPasteSpecialOptions((prev) => {
                        const newOpts = { ...prev, formulas: checked };
                        // If any individual is false, uncheck All
                        // If all three are true, check All
                        newOpts.all =
                          newOpts.formulas && newOpts.values && newOpts.formats;
                        return newOpts;
                      });
                    }}
                  />
                </div>

                <div style={{ marginBottom: "4px" }}>
                  <Checkbox
                    label={t("excelModals.pasteSpecial.values")}
                    checked={pasteSpecialOptions.values}
                    onChange={(e, { checked }) => {
                      setPasteSpecialOptions((prev) => {
                        const newOpts = { ...prev, values: checked };
                        newOpts.all =
                          newOpts.formulas && newOpts.values && newOpts.formats;
                        return newOpts;
                      });
                    }}
                  />
                </div>

                <div style={{ marginBottom: "4px" }}>
                  <Checkbox
                    label={t("excelModals.pasteSpecial.formats")}
                    checked={pasteSpecialOptions.formats}
                    onChange={(e, { checked }) => {
                      setPasteSpecialOptions((prev) => {
                        const newOpts = { ...prev, formats: checked };
                        newOpts.all =
                          newOpts.formulas && newOpts.values && newOpts.formats;
                        return newOpts;
                      });
                    }}
                  />
                </div>

                <div style={{ marginBottom: "0" }}>
                  <Checkbox
                    label={t("excelModals.pasteSpecial.all")}
                    checked={pasteSpecialOptions.all}
                    onChange={(e, { checked }) => {
                      setPasteSpecialOptions({
                        formulas: checked,
                        values: checked,
                        formats: checked,
                        all: checked,
                      });
                    }}
                  />
                </div>
              </div>

              <div>
                <Modal.Actions>
                  <Button onClick={handlePasteSpecialOK}>
                    {t("excelModals.common.ok")}
                  </Button>
                  <Button onClick={() => setShowPasteSpecialModal(false)}>
                    {t("excelModals.common.cancel")}
                  </Button>
                </Modal.Actions>
              </div>
            </div>
          </Modal.Content>
        </Modal>

        {/* Row Height Modal */}
        <Modal
          id="ExcelEditorRowHeightModal"
          open={showRowHeightModal}
          onClose={() => setShowRowHeightModal(false)}
          size="small"
        >
          <Modal.Header>{t("excelModals.rowHeight.title")}</Modal.Header>
          <Modal.Content>
            <div className="tw-flex tw-flex-row tw-justify-between">
              <div className="options-container tw-relative">
                <div className="title tw-fixed tw-top-[37px] tw-bg-[#f1f1f1] tw-px-[2px] tw-mt-[1px] tw-text-black tw-font-normal tw-leading-[16px]">
                  {t("excelModals.rowHeight.heightLabel")}
                </div>

                <Radio
                  label={t("excelModals.rowHeight.auto")}
                  checked={rowHeightMode === "auto"}
                  onChange={() => {
                    setRowHeightMode("auto");
                    setRowHeightValue(DEFAULT_ROW_HEIGHT);
                  }}
                />
                <Radio
                  label={t("excelModals.rowHeight.custom")}
                  checked={rowHeightMode === "custom"}
                  onChange={() => setRowHeightMode("custom")}
                />
                <div className="tw-mt-3">
                  <Input
                    label={{
                      basic: true,
                      content: t("excelModals.rowHeight.pixels"),
                    }}
                    labelPosition="left"
                    type="number"
                    min="0"
                    value={rowHeightValue}
                    disabled={rowHeightMode === "auto"}
                    onChange={(e, { value }) =>
                      setRowHeightValue(value === "" ? "" : Number(value))
                    }
                    style={{ width: "100px" }}
                  />
                </div>
              </div>

              <div>
                <Modal.Actions>
                  <Button onClick={applyRowHeight}>
                    {t("excelModals.common.ok")}
                  </Button>
                  <Button onClick={() => setShowRowHeightModal(false)}>
                    {t("excelModals.common.cancel")}
                  </Button>
                </Modal.Actions>
              </div>
            </div>
          </Modal.Content>
        </Modal>

        {/* Column Width Modal */}
        <Modal
          id="ExcelEditorColWidthModal"
          open={showColWidthModal}
          onClose={() => setShowColWidthModal(false)}
          size="small"
        >
          <Modal.Header>{t("excelModals.columnWidth.title")}</Modal.Header>
          <Modal.Content>
            <div className="tw-flex tw-flex-row tw-justify-between">
              <div className="options-container tw-relative">
                <div className="title tw-fixed tw-top-[37px] tw-bg-[#f1f1f1] tw-px-[2px] tw-mt-[1px] tw-text-black tw-font-normal tw-leading-[16px]">
                  {t("excelModals.columnWidth.widthLabel")}
                </div>
                <Radio
                  label={t("excelModals.columnWidth.auto")}
                  checked={colWidthMode === "auto"}
                  onChange={() => {
                    setColWidthMode("auto");
                    setColWidthValue(DEFAULT_COL_WIDTH);
                  }}
                />
                <Radio
                  label={t("excelModals.columnWidth.custom")}
                  checked={colWidthMode === "custom"}
                  onChange={() => setColWidthMode("custom")}
                />
                <div className="tw-mt-3">
                  <Input
                    label={{
                      basic: true,
                      content: t("excelModals.columnWidth.pixels"),
                    }}
                    labelPosition="left"
                    type="number"
                    min="0"
                    value={colWidthValue}
                    disabled={colWidthMode === "auto"}
                    onChange={(e, { value }) =>
                      setColWidthValue(value === "" ? "" : Number(value))
                    }
                    style={{ width: "100px" }}
                  />
                </div>
              </div>

              <div>
                <Modal.Actions>
                  <Button onClick={applyColWidth}>
                    {t("excelModals.common.ok")}
                  </Button>
                  <Button onClick={() => setShowColWidthModal(false)}>
                    {t("excelModals.common.cancel")}
                  </Button>
                </Modal.Actions>
              </div>
            </div>
          </Modal.Content>
        </Modal>

        <Workbook
          ref={ref}
          data={innerData}
          allowEdit={!readOnly}
          column={40}
          row={300}
          defaultColWidth={DEFAULT_COL_WIDTH}
          // defaultRowHeight={DEFAULT_ROW_HEIGHT} // Chnaging defaultRowHeight will cause scroll up crash
          showInfobar={false} // Hide default title bar
          showToolbar={true}
          toolbarItems={toolbarItems}
          customToolbarItems={customToolbarItems}
          showSheetTabs={true} // for zoom settings
          lang="en"
          onChange={handleChange}
          hooks={{
            beforeCellMouseDown: (_cell, cellInfo) => {
              const picker = formulaReferencePickerRef.current;
              if (
                picker?.selectCell?.(cellInfo.row, cellInfo.column) === true
              ) {
                return false;
              }
              return undefined;
            },
            afterUpdateCell: (row, column, oldValue, newValue) => {
              const numericValue =
                typeof newValue === "number" ? newValue : newValue?.v;
              const numberValue =
                typeof numericValue === "string"
                  ? Number(numericValue.replace(/,/g, ""))
                  : numericValue;
              const expectedFormat = getNumberFormat(numberValue);
              const alreadyFormatted =
                newValue?.ct?.fa === expectedFormat &&
                newValue?.ct?.t === "n";

              if (!Number.isFinite(numberValue) || alreadyFormatted) return;

              // Run after Fortune Sheet finishes committing the value so the
              // number format does not interfere with its active edit cycle.
              setTimeout(() => {
                ref.current?.setCellFormat?.(row, column, "ct", {
                  ...newValue?.ct,
                  fa: expectedFormat,
                  t: "n",
                });
              }, 0);
            },
            afterRenderCell: (cell, cellInfo, ctx) => {
              // If a formula evaluates to #VALUE! (e.g. =C3-10 with C3 empty),
              // overdraw only the content area (1px inset) with the cell background
              // so the error text is hidden while all borders remain intact.
              // The formula is untouched; when the referenced cell is filled,
              // fortune-sheet re-evaluates and the real value renders automatically.
              if (!cell?.f || !isValueError(cell.v)) return;
              const { startX, startY, endX, endY } = cellInfo;
              ctx.fillStyle = cell.bg || "#ffffff";
              ctx.fillRect(startX + 1, startY + 1, endX - startX - 2, endY - startY - 2);
            },
          }}
        />
      </div>
    </div>
  );
};

export default SpreadsheetEditorNew;
